/** `GET /api/agent/dashboard/summary` response — bucket counts for a date range. */
export interface AgentDashboardSummary {
  from: string;
  to: string;
  totalIncoming: number;
  served: number;
  ignored: number;
  missed: number;
  spam: number;
  inFlight: number;
}

/** One point in `GET /api/agent/dashboard/trend`'s response array. */
export interface AgentDashboardTrendPoint {
  bucket: string;
  total: number;
  spam: number;
}

export type AgentDashboardGranularity = 'day' | 'week' | 'month';

/** `GET /api/agent/dashboard/sla` response — median/max only, no average
 *  (a single forgotten request would skew an average badly). */
export interface AgentDashboardSla {
  medianTimeToClaimMinutes: number | null;
  maxTimeToClaimMinutes: number | null;
  medianTimeToResolveMinutes: number | null;
  maxTimeToResolveMinutes: number | null;
  escalationCount: number;
}

export type AgentDashboardBucket = 'served' | 'ignored' | 'missed' | 'spam' | 'in_flight';

/** One row in `GET /api/agent/dashboard/recent`'s response array. `document`
 *  is `null` when no `AgentCases.document_id` exists yet, or the document
 *  it pointed to was purged. `status` is the raw `AgentRequests.status` --
 *  needed alongside `bucket` because both `'claimed'` (actively being
 *  worked) and a fresh `'pending_intake'` row (not yet stale) fall into the
 *  same `in_flight` bucket; only a `status === 'claimed'` row gets the
 *  "Open in WebYTO" link. */
export interface AgentDashboardRecentItem {
  id: number;
  receivedAt: string;
  senderEmail: string | null;
  senderType: string | null;
  status: string;
  customerName: string | null;
  bucket: AgentDashboardBucket;
  claimedByName: string | null;
  outcome: string | null;
  document: { docId: number; docType: number; docNumber: string | null } | null;
}

/** `POST /api/agent/dashboard/requests/:id/claim|ignore|spam` response. */
export interface AgentDashboardActionResult {
  id: number;
  status: 'claimed' | 'ignored' | 'spam';
  reason?: string;
}

/** Same values as webyto-agent's card-builder.ts `IGNORE_REASONS` (API
 *  contract -- `value` is what `POST .../ignore` expects); `title` here is
 *  Ukrainian to match this component's existing UI language, not the
 *  Teams card's English titles. */
export const AGENT_DASHBOARD_IGNORE_REASONS: ReadonlyArray<{ title: string; value: string }> = [
  { title: 'Не лід / не той тред', value: 'not_a_lead' },
  { title: 'Програно конкуренту', value: 'lost_to_competitor' },
  { title: 'Спам / нерелевантно', value: 'spam' },
  { title: 'Дублікат звернення', value: 'duplicate' },
  { title: 'Інше', value: 'other' },
];
